import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  LifeBuoy,
  LayoutDashboard,
  LogOut,
  Menu,
  Plug,
  Plus,
  Shield,
  ShieldCheck,
  UserCog,
  Users,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { loadDashboard } from "@/utils/navigation";
import UserClass from "@/models/user";
import CognitoLoginClass from "@/models/cognitoLogin";
import SupportTicketModal from "@/components/support/SupportTicketModal";
import UserAvatar from "@/components/common/UserAvatar";
import {
  ADD_APP_PAGE,
  BILLING_PAGE,
  DASHBOARD_PAGE,
  INTEGRATIONS_PAGE,
  LOG_OUT_PAGE,
  MANAGE_APPS_PAGE,
  MANAGE_USERS_PAGE,
  USER_PAGE,
} from "@/constants/definitions";

interface MenubarProps {
  cognitologin: CognitoLoginClass;
  user: UserClass;
  navigate: (path: string) => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

// Top-level items, always shown to all signed-in users. Admin items
// (Integrations, Manage Users) live behind the Admin dropdown rather
// than the top bar; Add App stays top-level because it's a primary
// verb users press regularly, not a chores-menu task.
const PRIMARY_NAV: NavItem[] = [
  { label: "Dashboard",    path: DASHBOARD_PAGE,    icon: <LayoutDashboard className="h-4 w-4" /> },
  { label: "Applications", path: MANAGE_APPS_PAGE,  icon: <Shield className="h-4 w-4" /> },
];

// Admin-only items, shown only via the Admin dropdown (which itself
// only renders for users with the Admin / Account-Owner role). RoleGate
// still gates the route itself — both layers are required: hiding
// alone leaves the URL reachable; route gating alone leaves a
// Pentester staring at a nav link they can't use.
const ADMIN_NAV: NavItem[] = [
  { label: "Integrations",  path: INTEGRATIONS_PAGE,  icon: <Plug className="h-4 w-4" /> },
  { label: "Manage Users",  path: MANAGE_USERS_PAGE,  icon: <Users className="h-4 w-4" /> },
];

// Billing lives in the Admin dropdown alongside the other tenant-level
// surfaces, NOT in the avatar menu — that menu is personal (my account,
// support, log out) and billing is an organization concern.
//
// It is deliberately kept OUT of ADMIN_NAV: that array renders under
// isUserAdmin (Account Owner + Sys Admin), whereas Billing is gated on
// the narrower isAccountOwner. Folding it into the array would silently
// show the link to Sys Admins, who would then bounce off OwnerGate.
// Account Owners are a subset of admins, so the dropdown that hosts it
// is always present for anyone entitled to see it.
const BILLING_NAV: NavItem = {
  label: "Billing",
  path: BILLING_PAGE,
  icon: <Wallet className="h-4 w-4" />,
};

// Add App sits between primary and admin: it's an admin action but a
// primary verb, so it stays as a top-level button when the user has
// the role to use it. Hidden from Pentesters via isUserAdmin.
const ADD_APP_NAV: NavItem = {
  label: "Add App",
  path: ADD_APP_PAGE,
  icon: <Plus className="h-4 w-4" />,
};

export default function Menubar({ cognitologin, user, navigate }: MenubarProps) {
  const currentPath = window.location.pathname;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  const adminMenuRef = useRef<HTMLDivElement | null>(null);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);

  // Body scroll lock while the mobile menu overlay is open. Captures
  // and restores the previous overflow value so apps embedded in a
  // host page that already pins overflow don't clobber it on close.
  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  // Outside-click and Escape close for both dropdowns. mousedown so
  // we close before any downstream click handler runs (e.g. clicking
  // a tile elsewhere on the page).
  useEffect(() => {
    if (!adminMenuOpen && !accountMenuOpen) return undefined;
    const handleClick = (clickEvent: MouseEvent) => {
      const target = clickEvent.target as Node | null;
      if (adminMenuOpen && !adminMenuRef.current?.contains(target ?? null)) {
        setAdminMenuOpen(false);
      }
      if (accountMenuOpen && !accountMenuRef.current?.contains(target ?? null)) {
        setAccountMenuOpen(false);
      }
    };
    const handleKey = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key === "Escape") {
        setAdminMenuOpen(false);
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [adminMenuOpen, accountMenuOpen]);

  const handleNavigation = useCallback(
    async (path: string) => {
      setMobileOpen(false);
      setAdminMenuOpen(false);
      setAccountMenuOpen(false);
      if (path === LOG_OUT_PAGE) {
        // Await signOut so tokens are fully cleared BEFORE navigation
        // triggers React to re-render. Without this, Amplify's
        // InternalGraphQLAPIClass calls Auth.currentSession() on
        // in-flight API requests while the session is being revoked,
        // producing an uncaught "No current user" rejection.
        await cognitologin.logout();
        navigate(LOG_OUT_PAGE);
        return;
      }
      if (currentPath === path) {
        // Re-clicking the active nav item (or the logo while already
        // on the dashboard) scrolls back to the top.
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (path === DASHBOARD_PAGE) {
        loadDashboard(navigate);
      } else {
        navigate(path);
      }
    },
    [currentPath, cognitologin, navigate]
  );

  const isActive = (path: string) => {
    if (path === DASHBOARD_PAGE) return currentPath === DASHBOARD_PAGE;
    return currentPath.startsWith(path);
  };

  const isAdmin = user.isUserAdmin();
  const isOwner = user.isAccountOwner();
  // Billing counts toward the Admin button's active state now that it
  // is hosted in that dropdown — otherwise the button reads as inactive
  // while the user is sitting on one of its own destinations.
  const isAdminSurfaceActive =
    ADMIN_NAV.some((item) => isActive(item.path)) ||
    (isOwner && isActive(BILLING_NAV.path));

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 pt-6 pb-6">
        {/* Logo */}
        <button
          type="button"
          onClick={() => handleNavigation(DASHBOARD_PAGE)}
          className="flex shrink-0 items-center"
        >
          <img
            src="/nimis-intelligence-navy-logo.png"
            alt="NIMIS Intelligence"
            className="h-12 w-auto"
          />
        </button>

        {/* Desktop nav */}
        <div role="navigation" className="items-center gap-1 flex max-lg:!hidden">
          {PRIMARY_NAV.map((item) => (
            <button
              key={item.path}
              type="button"
              onClick={() => handleNavigation(item.path)}
              className={[
                "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                isActive(item.path)
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
              ].join(" ")}
            >
              {item.icon}
              {item.label}
            </button>
          ))}

          {isAdmin && (
            <button
              type="button"
              onClick={() => handleNavigation(ADD_APP_NAV.path)}
              className={[
                "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                isActive(ADD_APP_NAV.path)
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
              ].join(" ")}
            >
              {ADD_APP_NAV.icon}
              {ADD_APP_NAV.label}
            </button>
          )}

          {isAdmin && (
            <div className="relative" ref={adminMenuRef}>
              <button
                type="button"
                onClick={() => setAdminMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={adminMenuOpen}
                className={[
                  "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isAdminSurfaceActive
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                ].join(" ")}
              >
                <ShieldCheck className="h-4 w-4" />
                Admin
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${
                    adminMenuOpen ? "rotate-180" : ""
                  }`}
                  aria-hidden="true"
                />
              </button>
              {adminMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-1 w-48 rounded-md border border-gray-200 bg-white shadow-lg py-1 z-50"
                >
                  {ADMIN_NAV.map((item) => (
                    <button
                      key={item.path}
                      type="button"
                      role="menuitem"
                      onClick={() => handleNavigation(item.path)}
                      className={[
                        "flex w-full items-center gap-2 px-3 py-2 text-sm text-left",
                        isActive(item.path)
                          ? "bg-blue-50 text-blue-700"
                          : "text-gray-700 hover:bg-gray-50",
                      ].join(" ")}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  ))}
                  {isOwner && (
                    <>
                      {/* Divider marks the narrower gate: everything
                          above is admin-visible, Billing is owner-only. */}
                      <div className="my-1 border-t border-gray-100" />
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => handleNavigation(BILLING_NAV.path)}
                        className={[
                          "flex w-full items-center gap-2 px-3 py-2 text-sm text-left",
                          isActive(BILLING_NAV.path)
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-700 hover:bg-gray-50",
                        ].join(" ")}
                      >
                        {BILLING_NAV.icon}
                        {BILLING_NAV.label}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right side — avatar dropdown, scoped to the person: their
            account, support, log out. Organization-level surfaces
            (including Billing) live in the Admin dropdown. */}
        <div className="items-center gap-3 flex max-lg:!hidden">
          <div className="relative" ref={accountMenuRef}>
            <button
              type="button"
              onClick={() => setAccountMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={accountMenuOpen}
              className="inline-flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <UserAvatar
                avatarUrl={user.avatar}
                className="h-7 w-7 rounded-full object-cover ring-1 ring-gray-200"
                alt={user.displayName}
              />
              <span className="max-w-[120px] truncate">{user.displayName}</span>
              <ChevronDown
                className={`h-3.5 w-3.5 text-gray-400 transition-transform ${
                  accountMenuOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </button>
            {accountMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-1 w-52 rounded-md border border-gray-200 bg-white shadow-lg py-1 z-50"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleNavigation(USER_PAGE)}
                  className={[
                    "flex w-full items-center gap-2 px-3 py-2 text-sm text-left",
                    isActive(USER_PAGE)
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:bg-gray-50",
                  ].join(" ")}
                >
                  <UserCog className="h-4 w-4" />
                  Account
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setAccountMenuOpen(false);
                    setSupportModalOpen(true);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-50"
                >
                  <LifeBuoy className="h-4 w-4" />
                  Support
                </button>
                <div className="my-1 border-t border-gray-100" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleNavigation(LOG_OUT_PAGE)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="inline-flex items-center justify-center rounded-lg p-2 text-gray-600 hover:bg-gray-50 lg:!hidden"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile dropdown — overlays the page. The translucent backdrop
          sits below the header (top-16 matches h-16) and dismisses
          the menu on click; the menu panel itself is absolutely
          positioned just below the header at z-50 so it sits above
          page content but below any modals (which use higher
          z-indexes). */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-x-0 top-16 bottom-0 z-40 bg-black/30 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute top-full left-0 right-0 z-50 border-t border-gray-200 bg-white shadow-lg px-4 pb-4 pt-2 lg:hidden">
            {/* User identity (left) + logout (right) — sits above the
                nav list so the user can immediately see who they're
                signed in as and log out without scrolling past the
                navigation. */}
            <div className="flex items-center justify-between gap-3 pb-3 border-b border-gray-100">
              <button
                type="button"
                onClick={() => handleNavigation(USER_PAGE)}
                className="inline-flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 text-left min-w-0 flex-1"
              >
                <UserAvatar
                  avatarUrl={user.avatar}
                  className="h-7 w-7 rounded-full object-cover ring-1 ring-gray-200 shrink-0"
                  alt={user.displayName}
                />
                <span className="truncate">{user.displayName}</span>
              </button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleNavigation(LOG_OUT_PAGE)}
                className="gap-1.5 text-gray-600 shrink-0"
              >
                <LogOut className="h-3.5 w-3.5" />
                Log out
              </Button>
            </div>

            <div role="navigation" className="flex flex-col gap-1 pt-3">
              {PRIMARY_NAV.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => handleNavigation(item.path)}
                  className={[
                    "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors text-left",
                    isActive(item.path)
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  ].join(" ")}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}

              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  setSupportModalOpen(true);
                }}
                className={[
                  "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors text-left",
                  "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                ].join(" ")}
              >
                <LifeBuoy className="h-4 w-4" />
                Support
              </button>

              {isAdmin && (
                <button
                  type="button"
                  onClick={() => handleNavigation(ADD_APP_NAV.path)}
                  className={[
                    "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors text-left",
                    isActive(ADD_APP_NAV.path)
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  ].join(" ")}
                >
                  {ADD_APP_NAV.icon}
                  {ADD_APP_NAV.label}
                </button>
              )}

              {isAdmin && (
                <>
                  <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    Admin
                  </div>
                  {ADMIN_NAV.map((item) => (
                    <button
                      key={item.path}
                      type="button"
                      onClick={() => handleNavigation(item.path)}
                      className={[
                        "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors text-left",
                        isActive(item.path)
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                      ].join(" ")}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  ))}
                  {/* Owner-only, but grouped under Admin to match the
                      desktop dropdown — one home for Billing. */}
                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => handleNavigation(BILLING_NAV.path)}
                      className={[
                        "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors text-left",
                        isActive(BILLING_NAV.path)
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                      ].join(" ")}
                    >
                      {BILLING_NAV.icon}
                      {BILLING_NAV.label}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      <SupportTicketModal
        open={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        user={user}
      />
    </header>
  );
}
